import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { matchRoutes, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { InstallBotDialog } from '@components/Bots/InstallBotDialog'
import { ClipboardCopy } from '@components/ClipboardCopy/ClipboardCopy'
import { ConfirmBanUnbanModal } from '@components/ConfirmBanUnbanModal/ConfirmBanUnbanModal'
import { ConfirmBlockModal } from '@components/ConfirmBlockModal/ConfirmBlockModal'
import { useMatchingMessages } from '@components/DirectMessages/CreateDirectMessage/hooks/useMatchingMessages'
import { ErrorReportForm } from '@components/ErrorReport/ErrorReport'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { TownNotificationsButton } from '@components/NotificationSettings/NotificationsSettingsButton'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { PanelStack } from '@components/Panel/PanelContext'
import { ThemeSettingsDropdown } from '@components/ThemeSettings/ThemeSettingsDropdown'
import { UserPreferences } from '@components/UserProfile/UserPreferences'
import { UninstallBotButton, UserProfile } from '@components/UserProfile/UserProfile'
import { WalletLinkingPanel } from '@components/Web3/WalletLinkingPanel'
import { usePrivy } from '@towns/privy'
import { Box, Button, Icon, Paragraph, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useBlockedUsers } from 'hooks/useBlockedUsers'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import { useEnvironment } from 'hooks/useEnvironment'
import { usePushNotifications } from 'hooks/usePushNotifications'
import { useGetUserBio } from 'hooks/useUserBio'
import { useCombinedAuth } from 'privy/useCombinedAuth'
import { GetSigner } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS, PATHS } from 'routes'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    type Address,
    BASE_SEPOLIA,
    BlockchainTransactionType,
    LOCALHOST_CHAIN_ID,
    Permission,
    SpaceAddressFromSpaceId,
    SpaceData,
    useBanTransaction,
    useBotData,
    useConnectivity,
    useDmsAndGdms,
    useHasPermission,
    useInstalledApps,
    useIsSpaceOwner,
    useIsTransactionPending,
    useMyProfile,
    useSpaceData,
    useTownsClient,
    useUnbanTransaction,
    useUserLookupContext,
    useWalletAddressIsBanned,
} from 'use-towns-client'
import { env } from 'utils'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { shortAddress } from 'workers/utils'

import { PrivyIdentityPanel } from 'components/PrivyIdentity/PrivyIdentityPanel'
import { SubscriptionsPanel } from 'components/Web3/Subscriptions/SubscriptionsPanel'

import { usePanelActions } from './layouts/hooks/usePanelActions'

export const SpaceProfilePanel = () => {
    return (
        <Panel label="Profile">
            <SpaceProfile />
        </Panel>
    )
}

export const SpaceProfile = React.memo(() => <SpaceProfileWithoutAuth />)

enum ModalType {
    Wallets = 'wallets',
    Preferences = 'preferences',
    ReportBug = 'report-bug',
    Identity = 'identity',
    Subscriptions = 'subscriptions',
}

const SpaceProfileWithoutAuth = () => {
    const { client } = useTownsClient()
    const [search] = useSearchParams()
    const space = useSpaceData()
    const { baseChain } = useEnvironment()
    const cameFromSpaceInfoPanel = search.get('spaceInfo') !== null
    const [searchParams] = useSearchParams()
    const profileIdFromPath = searchParams.get('profileId') || 'me'
    const userId = profileIdFromPath
    const spaceAddress = space ? (SpaceAddressFromSpaceId(space.id) as Address) : undefined

    const [modal, setModal] = useState<ModalType | undefined>(undefined)
    const [showInstallBotDialog, setShowInstallBotDialog] = useState(false)

    const { requestPushPermission, simplifiedPermissionState } = usePushNotifications()

    const navigate = useNavigate()

    const onBack = useEvent(() => {
        navigate(-1)
    })

    const { logout } = useCombinedAuth()
    const { logout: privyLogout, authenticated: privyAuthenticated } = usePrivy()
    const { loggedInWalletAddress } = useConnectivity()

    const onLogoutClick = useEvent(() => {
        const analytics = Analytics.getInstance()
        analytics.track('clicked logout', {}, () => {
            console.log('[analytics] clicked logout')
        })
        analytics.reset()
        logout()
    })

    const onDeveloperPortalClick = useEvent(() => {
        navigate(`/${PATHS.DEVELOPER}/dashboard`)
    })

    const onReportBugClick = useEvent(() => {
        setModal(ModalType.ReportBug)
    })

    const onSwitchToAppClick = useEvent(() => {
        navigate('/')
    })

    const { createLink } = useCreateLink()
    const { lookupUser } = useUserLookupContext()

    const onMessageClick = useCallback(async () => {
        if (!userId) {
            return
        }
        const link = createLink({ messageId: 'new' })
        if (link) {
            const appAddress = lookupUser(userId)?.appAddress
            const params = new URLSearchParams({ to: userId })
            if (appAddress) {
                params.set('appAddress', appAddress)
            }
            navigate(`${link}?${params.toString()}`)
        }
    }, [createLink, lookupUser, navigate, userId])

    const myUser = useMyProfile()

    const location = useLocation()
    const isDeveloperRoute = matchRoutes([{ path: '/developer/*' }], location)
    const isMeRoute = matchRoutes([{ path: '/me' }], location) || profileIdFromPath === 'me'

    const { openPanel } = usePanelActions()

    const onWalletLinkingClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.WALLETS, { stackId: PanelStack.PROFILE })
    })

    const onIdentityClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.IDENTITY, { stackId: PanelStack.PROFILE })
    })

    const onSubscriptionsClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.SUBSCRIPTIONS, { stackId: PanelStack.PROFILE })
    })

    const onPreferencesClick = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.PREFERENCES, { stackId: PanelStack.PROFILE })
    })

    const user = useMemo(() => {
        if (isMeRoute) {
            return {
                ...myUser,
                userId: myUser?.userId ?? '',
                displayName: myUser?.displayName ?? '',
            }
        }
        if (userId) {
            return lookupUser(userId)
        }
    }, [isMeRoute, userId, myUser, lookupUser])

    const isValid = !!user

    const { isBanned, isLoading: isLoadingBanStatus } = useWalletAddressIsBanned(
        space?.id,
        user?.userId,
    )

    const { hasPermission: canBan } = useHasPermission({
        spaceId: space?.id ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.ModifyBanning,
    })

    const { data: userBio } = useGetUserBio(user?.userId)

    const canEdit = !!client && user?.userId === loggedInWalletAddress

    const isCurrentUser = user?.userId === myUser?.userId

    const { isTouch } = useDevice()

    const isUserBlocked = useBlockedUsers()
    const isBlocked: boolean = isUserBlocked(userId)

    const { data: myAbstractWalletAddress } = useMyAbstractAccountAddress()
    const { isOwner: isSpaceOwner } = useIsSpaceOwner(space?.id, myAbstractWalletAddress)

    const isDMContext = useMatch(`/${PATHS.MESSAGES}/*`) !== null

    const { data: installedAppsOnUser } = useInstalledApps(myAbstractWalletAddress)
    const isBotInstalledOnUser = useMemo(() => {
        if (!user?.isBot || !user.userId) {
            return false
        }
        return installedAppsOnUser?.has(user.userId.toLowerCase()) ?? false
    }, [user?.isBot, user?.userId, installedAppsOnUser])

    useEffect(() => {
        if (
            isCurrentUser &&
            (simplifiedPermissionState === 'soft-denied' ||
                simplifiedPermissionState === 'hard-denied')
        ) {
            Analytics.getInstance().page(
                'toast',
                'toast shown to enable notifications',
                {
                    spaceId: space?.id ?? 'undefined',
                },
                () => {
                    console.log('[analytics] toast shown to enable notifications', {
                        spaceId: space?.id ?? 'undefined',
                    })
                },
            )
        }
    }, [isCurrentUser, simplifiedPermissionState, space?.id])

    const onViewBotPageClick = useCallback(() => {
        if (user?.appAddress) {
            if (isTouch) {
                openPanel(CHANNEL_INFO_PARAMS.BOT_PAGE, { profileId: user.appAddress })
            } else {
                navigate(`/${PATHS.BOT}/${user?.appAddress}`)
            }
        }
    }, [isTouch, navigate, openPanel, user?.appAddress])

    const onBotSettingsClick = useCallback(() => {
        if (user?.appAddress) {
            openPanel(CHANNEL_INFO_PARAMS.BOT_SETTINGS, {
                profileId: user.appAddress,
                data: user.userId,
            })
        }
    }, [openPanel, user?.appAddress, user?.userId])

    const { data: botData } = useBotData(user?.appAddress)

    const onInstallBotClick = useCallback(() => {
        setShowInstallBotDialog(true)
    }, [])

    const { dmsAndGdms } = useDmsAndGdms()
    const { matchingDM } = useMatchingMessages({
        selectedUserArray: [userId ?? ''],
        dmChannels: dmsAndGdms,
    })

    return (
        <Stack gap>
            {isValid ? (
                <UserProfile
                    center
                    key={user.userId}
                    userId={user.userId}
                    displayName={getPrettyDisplayName(user)}
                    userBio={userBio}
                    canEdit={canEdit}
                />
            ) : (
                <Stack padding>
                    <Box padding border="negative" rounded="xs">
                        <Paragraph color="error">Profile not found</Paragraph>
                        <Paragraph color="gray2" size="xs">
                            {profileIdFromPath && (
                                <>
                                    <ClipboardCopy
                                        label={shortAddress(profileIdFromPath)}
                                        clipboardContent={profileIdFromPath}
                                        fontSize="sm"
                                    />
                                    {userId && (
                                        <ClipboardCopy
                                            label={`resolved: ${shortAddress(userId)}`}
                                            clipboardContent={userId}
                                            fontSize="sm"
                                        />
                                    )}
                                </>
                            )}
                        </Paragraph>
                    </Box>
                </Stack>
            )}

            {cameFromSpaceInfoPanel && (
                <Box centerContent>
                    <Button
                        width="auto"
                        tone="none"
                        size="button_sm"
                        style={{
                            boxShadow: 'none',
                        }}
                        onClick={onBack}
                    >
                        <Text color="cta1">Back to space info</Text>
                    </Button>
                </Box>
            )}
            {isCurrentUser && (
                <Stack gap paddingBottom="md" paddingTop="none">
                    {/* wallets */}
                    <PanelButton onClick={onWalletLinkingClick}>
                        <Box width="height_md" alignItems="center">
                            <Icon type="wallet" size="square_sm" />
                        </Box>
                        <Paragraph>Linked Wallets</Paragraph>
                    </PanelButton>

                    <PanelButton onClick={onIdentityClick}>
                        <Box width="height_md" alignItems="center">
                            <Icon type="link" size="square_sm" />
                        </Box>
                        <Paragraph>Linked Accounts</Paragraph>
                    </PanelButton>

                    <PanelButton onClick={onSubscriptionsClick}>
                        <Box width="height_md" alignItems="center">
                            <Icon type="dollarFilled" size="square_sm" />
                        </Box>
                        <Paragraph>Memberships</Paragraph>
                    </PanelButton>

                    {simplifiedPermissionState === 'soft-denied' && (
                        <>
                            <PanelButton onClick={requestPushPermission}>
                                <Box width="height_md" alignItems="center">
                                    <Icon type="bell" size="square_sm" />
                                </Box>
                                <Paragraph>Enable push notifications</Paragraph>
                            </PanelButton>
                        </>
                    )}

                    {simplifiedPermissionState === 'hard-denied' && (
                        <PanelButton height="auto" onClick={requestPushPermission}>
                            <Stack horizontal gap="sm" alignItems="center">
                                <Box width="height_md" alignItems="center">
                                    <Icon type="bell" size="square_sm" />
                                </Box>
                                <Stack>
                                    <Paragraph fontWeight="strong" size="md">
                                        Enable Push Notifications
                                    </Paragraph>

                                    <Paragraph size="md">
                                        Look for the padlock or <strong>Secure</strong> sign in the
                                        address bar.
                                        <br />
                                        Click on it, and a menu will appear.
                                        <br />
                                        Find <strong>Notifications</strong> and choose{' '}
                                        <strong>Enable</strong>
                                    </Paragraph>
                                </Stack>
                            </Stack>
                        </PanelButton>
                    )}

                    <PanelButton onClick={onPreferencesClick}>
                        <Box width="height_md" alignItems="center">
                            <Icon type="settings" size="square_sm" />
                        </Box>
                        <Paragraph color="default">Preferences</Paragraph>
                    </PanelButton>

                    <PanelButton>
                        <ThemeSettingsDropdown />
                    </PanelButton>

                    {isDeveloperRoute ? (
                        <PanelButton onClick={onSwitchToAppClick}>
                            <Box width="height_md" alignItems="center">
                                <Icon type="home" size="square_sm" />
                            </Box>
                            <Paragraph color="default">Switch to App</Paragraph>
                        </PanelButton>
                    ) : (
                        <PanelButton onClick={onDeveloperPortalClick}>
                            <Box width="height_md" alignItems="center">
                                <Icon type="robot" size="square_sm" />
                            </Box>
                            <Paragraph>Switch to Developer Portal</Paragraph>
                        </PanelButton>
                    )}
                    {isTouch && (
                        <PanelButton
                            tone="negative"
                            data-testid="logout-button"
                            onClick={onReportBugClick}
                        >
                            <Box width="height_md" alignItems="center">
                                <Icon type="bug" size="square_sm" />
                            </Box>
                            <Paragraph>Report a bug</Paragraph>
                        </PanelButton>
                    )}

                    <PanelButton
                        tone="negative"
                        data-testid="logout-button"
                        onClick={onLogoutClick}
                    >
                        <Box width="height_md" alignItems="center">
                            <Icon type="logout" size="square_sm" />
                        </Box>
                        <Paragraph>Logout</Paragraph>
                    </PanelButton>

                    {(env.DEV ||
                        baseChain.id === BASE_SEPOLIA ||
                        baseChain.id === LOCALHOST_CHAIN_ID) &&
                        (privyAuthenticated ? (
                            <PanelButton tone="negative" onClick={privyLogout}>
                                <Box width="height_md" alignItems="center">
                                    <Icon type="logout" size="square_sm" />
                                </Box>
                                <Paragraph>Logout from Privy (TESTNET)</Paragraph>
                            </PanelButton>
                        ) : (
                            <Box horizontal disabled alignItems="center" gap="sm" cursor="default">
                                <Box width="height_md" alignItems="center">
                                    <Icon type="alert" size="square_sm" />
                                </Box>
                                <Paragraph>Privy not authenticated!</Paragraph>
                            </Box>
                        ))}
                </Stack>
            )}

            {modal === ModalType.Wallets && (
                <ModalContainer touchTitle="Wallets" onHide={() => setModal(undefined)}>
                    <WalletLinkingPanel />
                </ModalContainer>
            )}

            {modal === ModalType.Preferences && (
                <ModalContainer touchTitle="Preferences" onHide={() => setModal(undefined)}>
                    <Box padding gap>
                        <UserPreferences />
                    </Box>
                </ModalContainer>
            )}

            {modal === ModalType.ReportBug && (
                <ModalContainer touchTitle="Bug Report" onHide={() => setModal(undefined)}>
                    <Box padding gap>
                        <ErrorReportForm onHide={() => setModal(undefined)} />
                    </Box>
                </ModalContainer>
            )}

            {modal === ModalType.Identity && (
                <ModalContainer touchTitle="Linked Accounts" onHide={() => setModal(undefined)}>
                    <PrivyIdentityPanel />
                </ModalContainer>
            )}

            {modal === ModalType.Subscriptions && (
                <ModalContainer touchTitle="Subscriptions" onHide={() => setModal(undefined)}>
                    <SubscriptionsPanel />
                </ModalContainer>
            )}

            <Stack gap>
                {user?.isBot && (
                    <PanelButton onClick={onViewBotPageClick}>
                        <Icon type="robot" size="square_sm" color="gray2" />
                        <Paragraph color="default">View Bot Page</Paragraph>
                    </PanelButton>
                )}

                {/* Install Bot: only in space context (not DM) */}
                {user?.isBot && !isDMContext && (
                    <PanelButton onClick={onInstallBotClick}>
                        <Icon type="download" size="square_sm" color="gray2" />
                        <Paragraph color="default">Install Bot</Paragraph>
                    </PanelButton>
                )}

                {/* Uninstall Bot: only in DM context when bot is installed on user */}
                {user?.isBot &&
                    user.appAddress &&
                    isDMContext &&
                    isBotInstalledOnUser &&
                    myAbstractWalletAddress && (
                        <UninstallBotButton
                            appAddress={user.appAddress}
                            walletAddress={myAbstractWalletAddress}
                            installPrice={botData?.installPrice}
                        />
                    )}

                {user?.isBot && spaceAddress && isSpaceOwner && (
                    <PanelButton onClick={onBotSettingsClick}>
                        <Icon type="settings" size="square_sm" color="gray2" />
                        <Paragraph color="default">Bot Settings</Paragraph>
                    </PanelButton>
                )}

                {!isCurrentUser && !search.has('message') && user && (
                    <PanelButton onClick={onMessageClick}>
                        <Icon type="message" size="square_sm" color="gray2" />
                        <Paragraph color="default">Send Message</Paragraph>
                    </PanelButton>
                )}
                {!isCurrentUser && matchingDM && (
                    <TownNotificationsButton type="dm" channelId={matchingDM.id} />
                )}

                {!isCurrentUser && canBan && space && !isLoadingBanStatus && !user?.isBot && (
                    <BanPanelButton
                        walletAddress={user?.userId ?? ''}
                        isBanned={isBanned}
                        space={space}
                    />
                )}

                {!isCurrentUser && userId && (
                    <BlockPanelButton userId={userId} isBlocked={isBlocked} />
                )}
            </Stack>

            {user?.isBot && botData && (
                <InstallBotDialog
                    isOpen={showInstallBotDialog}
                    bot={botData}
                    target="space"
                    onClose={() => setShowInstallBotDialog(false)}
                />
            )}
        </Stack>
    )
}

const BanPanelButton = (props: { walletAddress: string; space: SpaceData; isBanned: boolean }) => {
    const { walletAddress, space, isBanned } = props
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
    const { banTransaction } = useBanTransaction()
    const { unbanTransaction } = useUnbanTransaction()

    const onClick = useCallback(() => {
        setIsConfirmModalVisible(true)
    }, [setIsConfirmModalVisible])

    const onCancelClick = useCallback(() => {
        setIsConfirmModalVisible(false)
    }, [setIsConfirmModalVisible])

    const onConfirmClick = useCallback(
        (getSigner: GetSigner) => {
            setIsConfirmModalVisible(false)
            if (!space?.id) {
                return
            }
            async function performTransaction(spaceId: string, userId: string) {
                const signer = await getSigner()
                if (!signer) {
                    createPrivyNotAuthenticatedNotification()
                    return
                }
                if (isBanned) {
                    unbanTransaction(signer, spaceId, userId)
                } else {
                    banTransaction(signer, spaceId, userId)
                }
            }
            void performTransaction(space.id, walletAddress)
        },
        [
            space?.id,
            walletAddress,
            banTransaction,
            unbanTransaction,
            isBanned,
            setIsConfirmModalVisible,
        ],
    )

    const isBanTransactionPending = useIsTransactionPending(BlockchainTransactionType.BanUser)
    const isUnbanTransactionPending = useIsTransactionPending(BlockchainTransactionType.UnbanUser)
    const isTransactionPending = isBanTransactionPending || isUnbanTransactionPending

    return (
        <>
            <PanelButton tone="negative" onClick={onClick}>
                <Icon type={isBanned ? 'unban' : 'ban'} size="square_sm" />
                {isTransactionPending ? (
                    <Box grow>
                        <ButtonSpinner />
                    </Box>
                ) : (
                    <Paragraph>
                        {isBanned ? 'Unban' : 'Ban'} from {space.name}
                    </Paragraph>
                )}
            </PanelButton>
            {isConfirmModalVisible === true && (
                <ConfirmBanUnbanModal
                    userId={walletAddress}
                    ban={!isBanned}
                    onConfirm={onConfirmClick}
                    onCancel={onCancelClick}
                />
            )}
        </>
    )
}

const BlockPanelButton = (props: { userId: string; isBlocked: boolean }) => {
    const { userId, isBlocked: isCurrentlyBlocked } = props
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false)
    const [isUserBlockRequestInFlight, setIsUserBlockRequestInFlight] = useState(false)
    const { updateUserBlock } = useTownsClient()

    const onClick = useCallback(async () => {
        if (isUserBlockRequestInFlight) {
            return
        }
        if (isCurrentlyBlocked) {
            setIsUserBlockRequestInFlight(true)
            // unblock user
            await updateUserBlock(userId, false)
            setIsUserBlockRequestInFlight(false)
        } else {
            setIsConfirmModalVisible(true)
        }
    }, [isCurrentlyBlocked, isUserBlockRequestInFlight, updateUserBlock, userId])

    const onCancelClick = useCallback(() => {
        setIsConfirmModalVisible(false)
    }, [setIsConfirmModalVisible])

    const onConfirmClick = useCallback(async () => {
        setIsConfirmModalVisible(false)
        setIsUserBlockRequestInFlight(true)
        // block user
        await updateUserBlock(userId, true)
        setIsUserBlockRequestInFlight(false)
    }, [updateUserBlock, userId])

    return (
        <>
            <PanelButton tone="negative" onClick={onClick}>
                <Icon type={isCurrentlyBlocked ? 'unblockHand' : 'blockHand'} size="square_sm" />
                <Paragraph>{isCurrentlyBlocked ? 'Unblock' : 'Block'}</Paragraph>
            </PanelButton>
            {isConfirmModalVisible === true && (
                <ConfirmBlockModal
                    userId={userId}
                    onConfirm={onConfirmClick}
                    onCancel={onCancelClick}
                />
            )}
        </>
    )
}
